import React, {useMemo, useRef} from 'react';
import SMCToggleSwitchStyles from './SMCToggleSwitchStyles'
import SMCField from "../SMCField";
import PropTypes from "prop-types";
import {useSMCDisabledContext} from '../SMCDisabledContext';
import {hasValue} from "../../../utils/utils";

/**
 *
 * @param {object} params
 * @param {boolean} [params.disabled]
 * @param {boolean} [params.ignoreDisabledContext]
 * @param {function} [params.onChange]
 * @param {boolean} [params.labelClickable]
 * @param {(string|number)} [params.labelHeight]
 * @param {('small'|'medium')} [params.size]
 * @param {boolean} [params.checked]
 * @param {string} [params.label]
 * @param {('start'|'end')} [params.labelPosition]
 * @param {(string|number)} [params.labelWidth]
 * @param {string} [params.tooltip]
 * @param {boolean} [params.inline]
 * @param {*} [params.innerRef]
 * @param {string} [params.className]
 * @param {string} [params.a}
 */
function SMCToggleSwitch(props) {
    let {
        disabled,
        ignoreDisabledContext,
        onChange,
        labelClickable = true,
        labelHeight,
        checked,
        label,
        size="small",
        labelPosition,
        labelWidth,
        tooltip,
        inline,
        innerRef,
        className,
        onIcon,
        offIcon,
        onBackgroundColor = '#56B40A',
        offBackgroundColor = '#B7B7B7',
    } = props;

    const disabledContext = useSMCDisabledContext();

    const toggleDisabled = useMemo(() => {
        if (disabledContext && !ignoreDisabledContext) {
            if (disabledContext.forceDisable) {
                return true;
            }
            if (disabledContext.forceEnable) {
                return false;
            }
        }
        if (hasValue(disabled)) {
            return disabled;
        }
        return undefined;
    }, [disabled, disabledContext, ignoreDisabledContext]);

    const labelProps = useMemo(() => {
        return {
            onBackgroundColor: onBackgroundColor,
            offBackgroundColor: offBackgroundColor,
            marginLeft: size === "medium" ? '19px': '13px',
            checked: checked,
            disabled: toggleDisabled
        }
    }, [toggleDisabled, checked, size, onBackgroundColor, offBackgroundColor]);

    const classes = SMCToggleSwitchStyles(labelProps);
    const ref = useRef();
    const toggleRef = innerRef || ref;

    const onClick = (e) => {
        if (labelClickable && !toggleDisabled) {
            e.target = toggleRef.current;
            e.currentTarget = toggleRef.current;
            let checkedValue = !toggleRef.current.checked
            e.target.checked = checkedValue;
            onChange && onChange(checkedValue);
        }
    }

    return (
        <div className={className}>
            <SMCField
                disabled={toggleDisabled}
                height={labelHeight}
                inline={inline}
                labelPosition={labelPosition === 'start' ? 'start' : 'end'}
                labelWidth={labelWidth}
                tooltip={tooltip}
                label={label}
                labelOnClick={onClick}
            >
                <div className={labelPosition === 'start' ? undefined : classes.wrapper}>
                    <div onClick={labelClickable ? onClick : undefined}>
                        <img alt="" src={onIcon} className={classes.onIcon}/>
                    </div>
                    <input
                        type="checkbox"
                        className={classes.toggle || ""}
                        ref={toggleRef}
                        defaultChecked={labelProps.checked}
                        onChange={onChange}>
                    </input>
                    <label onClick={onClick} className={classes.label}
                           style={{width: size === "medium" ? '40px' : '32px'}}>
                        <img alt="" src={offIcon} className={classes.offIcon}/>
                    </label>
                </div>
            </SMCField>
        </div>
    )
}

SMCToggleSwitch.propTypes = {
    disabled: PropTypes.bool,
    ignoreDisabledContext: PropTypes.bool,
    onChange: PropTypes.func,
    labelClickable: PropTypes.bool,
    labelHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    size: PropTypes.oneOf(['small', 'medium']),
    checked: PropTypes.bool,
    label: PropTypes.node,
    tooltip: PropTypes.node,
    labelPosition: PropTypes.oneOf(['start', 'end']),
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inline: PropTypes.bool,
    innerRef: PropTypes.node,
    className: PropTypes.string,
    onIcon: PropTypes.string,
    offIcon: PropTypes.string,
    onBackgroundColor: PropTypes.string,
    offBackgroundColor: PropTypes.string
}

export default React.memo(SMCToggleSwitch);

